/**
 * cart-add-popup.js
 *
 * Shows a centered modal when a customer adds a product carrying the trigger
 * tag (default "Bundles") to their cart. The customer-tag exclusion (default
 * "Business Partner") is enforced server-side in snippets/cart-add-popup.liquid
 * -- if the customer is excluded, the #cart-add-popup element and this script
 * are never rendered, so this file simply no-ops.
 *
 * Detection is surface-independent and runs on two paths:
 *
 *   1. PRIMARY -- intercept the response of the add request itself. The theme
 *      posts to /cart/add.js (axios, i.e. XHR) from every surface: product page,
 *      quick view, quick-add toolbar, cart add-ons. The response body carries
 *      the added line item(s), so we read the handle straight off it.
 *
 *   2. FALLBACK -- listen for the theme's `theme:cart:change` event and diff the
 *      new cart against the previous snapshot to find which line(s) grew, for
 *      any surface that adds without going through XHR/fetch.
 *
 * Path 1 exists because path 2 alone is racy: theme.js `handleSuccess` calls
 * updateHeaderTotal() (async GET /cart.js -> theme:cart:change) and, in the same
 * tick, reloadCart()/openPopdown() which dispatch theme:cart:reload. Our reload
 * handler re-baselines from its own GET /cart.js, so whichever of the two
 * responses landed first decided whether the diff still saw the add -- the
 * popup appeared only some of the time. Reading the add response removes the
 * ordering dependency entirely.
 *
 * Either path resolves the product's tags via /products/{handle}.js, since
 * neither cart.js nor cart/add.js exposes product tags. Lookups are cached.
 *
 * Inspect live state in the console via `window.cartAddPopup`.
 */
(function () {
  'use strict';

  var root = document.getElementById('cart-add-popup');
  if (!root) return; // gated out server-side (excluded customer or disabled)

  var PRODUCT_TAG = (root.getAttribute('data-product-tag') || '').toLowerCase().trim();
  var dialog = root.querySelector('[data-cap-dialog]');

  var lastItems = {}; // line item key -> quantity
  var tagCache = {}; // product handle -> boolean (has trigger tag)
  var initialized = false;

  function routes() {
    return (window.theme && window.theme.routes) || {};
  }

  function rootUrl() {
    var base = routes().root_url || '/';
    return base === '/' ? '' : base.replace(/\/$/, '');
  }

  function snapshot(cart) {
    var map = {};
    (cart.items || []).forEach(function (it) {
      map[it.key] = it.quantity;
    });
    return map;
  }

  function getCart() {
    var url = (routes().cart || '/cart') + '.js';
    return fetch(url, { headers: { Accept: 'application/json' } }).then(function (r) {
      return r.json();
    });
  }

  function resyncBaseline() {
    return getCart()
      .then(function (c) {
        lastItems = snapshot(c);
        initialized = true;
      })
      .catch(function () { initialized = true; });
  }

  // Returns a promise resolving to whether this product handle carries the tag.
  function productHasTag(handle) {
    if (!handle) return Promise.resolve(false);
    if (Object.prototype.hasOwnProperty.call(tagCache, handle)) {
      return Promise.resolve(tagCache[handle]);
    }
    return fetch(rootUrl() + '/products/' + handle + '.js', {
      headers: { Accept: 'application/json' },
    })
      .then(function (r) { return r.json(); })
      .then(function (p) {
        var has = (p.tags || []).some(function (t) {
          return String(t).toLowerCase().trim() === PRODUCT_TAG;
        });
        tagCache[handle] = has;
        return has;
      })
      .catch(function () { return false; });
  }

  // De-duplicate, then show the popup on the first tagged match.
  function checkHandles(handles) {
    var uniq = (handles || []).filter(function (h, i) {
      return h && handles.indexOf(h) === i;
    });
    (function checkNext(i) {
      if (i >= uniq.length) return;
      productHasTag(uniq[i]).then(function (has) {
        if (has) open();
        else checkNext(i + 1);
      });
    })(0);
  }

  // Diff the new cart against the prior snapshot; return handles whose quantity
  // increased (i.e. were just added). Always refreshes the snapshot.
  function findAddedHandles(cart) {
    var added = [];
    (cart.items || []).forEach(function (it) {
      var prev = lastItems[it.key] || 0;
      if (it.quantity > prev) added.push(it.handle);
    });
    lastItems = snapshot(cart);
    return added;
  }

  function open() {
    root.removeAttribute('hidden');
    document.documentElement.classList.add('cap-open');
    var closeBtn = root.querySelector('[data-cap-close]');
    if (closeBtn) closeBtn.focus();
  }

  function close() {
    root.setAttribute('hidden', '');
    document.documentElement.classList.remove('cap-open');
  }

  function handleCart(cart) {
    if (!cart) return;
    var added = findAddedHandles(cart);
    if (!initialized) { initialized = true; return; } // first observation is a baseline, not an add
    if (!added.length) return;
    checkHandles(added);
  }

  // --- Primary path: intercept the add request ------------------------------

  var ADD_URL = /\/cart\/add(\.js)?(\?|$)/;

  function isAddUrl(url) {
    if (!url) return false;
    return ADD_URL.test(String(url).split('#')[0]);
  }

  // /cart/add.js returns the added line item, or {items: [...]} for a bulk add.
  function handleAddPayload(data) {
    if (!data || typeof data !== 'object') return;
    var items = Array.isArray(data.items) ? data.items : [data];
    var handles = items.map(function (it) { return it && it.handle; }).filter(Boolean);
    if (!handles.length) return;
    checkHandles(handles);
    // Absorb this add into the baseline so the fallback diff doesn't re-fire on
    // the theme:cart:change that follows.
    resyncBaseline();
  }

  (function interceptXHR() {
    var proto = window.XMLHttpRequest && window.XMLHttpRequest.prototype;
    if (!proto || !proto.open || !proto.send) return;
    var nativeOpen = proto.open;
    var nativeSend = proto.send;

    proto.open = function (method, url) {
      try { this.__capIsAdd = isAddUrl(url); } catch (e) { this.__capIsAdd = false; }
      return nativeOpen.apply(this, arguments);
    };

    proto.send = function () {
      if (this.__capIsAdd) {
        this.addEventListener('load', function () {
          if (this.status < 200 || this.status >= 300) return;
          var data = this.response;
          if (typeof data === 'string') {
            try { data = JSON.parse(data); } catch (e) { return; }
          }
          handleAddPayload(data);
        });
      }
      return nativeSend.apply(this, arguments);
    };
  })();

  (function interceptFetch() {
    if (typeof window.fetch !== 'function') return;
    var nativeFetch = window.fetch;
    window.fetch = function (input, init) {
      var url = input && input.url ? input.url : input;
      var promise = nativeFetch.apply(this, arguments);
      if (isAddUrl(url)) {
        promise
          .then(function (res) {
            if (!res || !res.ok) return;
            res.clone().json().then(handleAddPayload).catch(function () {});
          })
          .catch(function () {});
      }
      return promise;
    };
  })();

  // --- Fallback path + UI wiring --------------------------------------------

  Array.prototype.forEach.call(root.querySelectorAll('[data-cap-close]'), function (el) {
    el.addEventListener('click', function (e) {
      e.preventDefault();
      close();
    });
  });

  // The primary button optionally empties the cart before following its link --
  // the shopper is being told they bought the wrong thing, so we clear the
  // bundle out on the way to the pack page. Navigation happens regardless of
  // whether the clear succeeds, so a failed request can never trap them here.
  Array.prototype.forEach.call(root.querySelectorAll('[data-cap-clear-cart]'), function (el) {
    el.addEventListener('click', function (e) {
      var href = el.getAttribute('href');
      if (!href || href.charAt(0) === '#') return;
      if (el.__capClearing) return; // already in flight from a double click
      e.preventDefault();
      el.__capClearing = true;

      var go = function () { window.location.href = href; };
      var url = (routes().cart || '/cart') + '/clear.js';

      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      })
        .then(go)
        .catch(go);
    });
  });

  // Click on the dimmed backdrop (outside the dialog) closes the modal.
  root.addEventListener('click', function (e) {
    if (dialog && !dialog.contains(e.target)) close();
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && !root.hasAttribute('hidden')) close();
  });

  document.addEventListener('theme:cart:change', function (e) {
    var cart = e.detail && e.detail.cart;
    if (cart) handleCart(cart);
    else getCart().then(handleCart);
  });

  // A full cart re-render is not necessarily an add; just resync the baseline.
  document.addEventListener('theme:cart:reload', function () {
    resyncBaseline();
  });

  if (document.readyState !== 'loading') resyncBaseline();
  else document.addEventListener('DOMContentLoaded', resyncBaseline);

  window.cartAddPopup = {
    open: open,
    close: close,
    check: checkHandles,
    get productTag() { return PRODUCT_TAG; },
    state: function () {
      return { initialized: initialized, lastItems: lastItems, tagCache: tagCache };
    },
  };
})();
